import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

const TermsOfService: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      title: '1. Acceptance of Terms',
      content: `These Terms of Service ("Terms") govern your access to and use of the applications, utilities, and games published by AppsLab LLC ("AppsLab," "we," "our," or "us"), as well as this website (collectively, the "Services"). By accessing or using any of our Services, you agree to be bound by these Terms. If you do not agree, do not use our Services.

These Terms constitute a legally binding agreement between you and AppsLab LLC.`
    },
    {
      title: '2. License Grant',
      content: `Subject to your compliance with these Terms, AppsLab LLC grants you a personal, non-exclusive, non-transferable, revocable license to use our Services for your personal or internal business purposes.

You may not:
• Modify, reverse engineer, decompile, or disassemble any of our Services
• Rent, lease, sell, sublicense, or redistribute any of our Services
• Use our Services for any unlawful purpose or in violation of applicable laws
• Remove or alter proprietary notices or labels on our Services`
    },
    {
      title: '3. Use of Our Services',
      content: `Our Services are provided for personal and, where indicated, commercial use. Some Services may require you to meet minimum device or operating system requirements to function correctly.

You acknowledge that:
• Feature availability may vary by Service, platform, and version
• We may modify, suspend, or discontinue features of any Service at any time
• We are not responsible for third-party hardware, software, or network issues affecting your use of our Services`
    },
    {
      title: '4. Privacy',
      content: `Your privacy is important to us. Our collection and use of information in connection with our Services is described in our Privacy Policy, which is incorporated into these Terms by reference.

By using our Services, you consent to the data practices described in the Privacy Policy.`
    },
    {
      title: '5. User Responsibilities',
      content: `You agree to:

• Use our Services in compliance with all applicable laws and regulations
• Not attempt to circumvent security features or limitations of any Service
• Not use our Services to harm, harass, or infringe on the rights of others
• Report any bugs or security vulnerabilities you discover

You are solely responsible for your use of our Services and any consequences that result from it.`
    },
    {
      title: '6. Intellectual Property',
      content: `All content, code, designs, and trademarks associated with our Services are the property of AppsLab LLC or its licensors and are protected by applicable intellectual property laws.

"AppsLab" and any associated names, logos, or trademarks may not be used without our prior written permission. Our Services may include open-source components used under their respective licenses.`
    },
    {
      title: '7. Disclaimers',
      content: `OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.

We do not warrant that our Services will be uninterrupted, error-free, or compatible with every device or operating system version. Your use of our Services is at your own discretion and risk.`
    },
    {
      title: '8. Limitation of Liability',
      content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW, APPSLAB LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE, OR DATA ARISING FROM YOUR USE OF OUR SERVICES.

In no event shall AppsLab LLC's total liability to you for all claims exceed $50 USD or the amount you paid us for the relevant Service in the preceding 12 months, whichever is greater.`
    },
    {
      title: '9. Indemnification',
      content: `You agree to indemnify and hold harmless AppsLab LLC and its officers, employees, and agents from any claims, damages, or expenses (including reasonable attorneys' fees) arising from your use of our Services or your violation of these Terms.`
    },
    {
      title: '10. Changes to Our Services and These Terms',
      content: `We may modify, suspend, or discontinue any Service, or update these Terms, at any time. Significant changes will be communicated where practical. Continued use of our Services after changes take effect constitutes acceptance of the revised Terms.`
    },
    {
      title: '11. Termination',
      content: `You may stop using our Services at any time. We may suspend or terminate your access to any Service if you violate these Terms, without liability to you, to the extent permitted by law.`
    },
    {
      title: '12. Governing Law',
      content: `These Terms are governed by the laws of the United States, without regard to conflict-of-law principles. Any disputes will be resolved through informal negotiation in good faith and, if unresolved, through binding arbitration, except where prohibited by applicable law.`
    },
    {
      title: '13. Contact Information',
      content: `If you have questions about these Terms, please contact us:

Email: contact@appslabllc.com
Website: https://appslabllc.com

We aim to respond to legitimate inquiries within 7 business days.

Last Updated: July 2026`
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-r from-teal-600 via-teal-700 to-ember-700 py-12 border-b border-teal-800/30">
        <div className="container mx-auto section-padding">
          <Link
            to="/"
            className="inline-flex items-center gap-2 mb-4 text-teal-100 hover:text-white transition-colors text-sm"
          >
            ← Back to Home
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold font-display mb-4 text-white">
              Terms of Service
            </h1>
            <p className="text-lg text-teal-100">
              Please read these terms carefully before using our Services.
            </p>
          </motion.div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto section-padding py-16">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-cloud-50 rounded-3xl border border-cloud-200 p-8 md:p-12">
            {sections.map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className={index < sections.length - 1 ? 'mb-12' : ''}
              >
                <h2 className="text-2xl md:text-3xl font-bold mb-4 text-teal-600 font-display">
                  {section.title}
                </h2>
                <div className="text-cloud-700 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA at bottom */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <p className="mb-6 text-cloud-600 text-lg">
              Want to learn more about AppsLab LLC?
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-600 to-teal-500 text-white font-bold rounded-xl hover:from-teal-500 hover:to-teal-400 transition-all shadow-lg shadow-teal-500/30"
            >
              Back to Home
            </Link>
          </motion.div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-cloud-200 py-8">
        <div className="container mx-auto section-padding">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-teal-700 rounded-lg flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-cloud-900 font-display">AppsLab</span>
            </div>
            <div className="flex gap-6 text-sm text-cloud-500">
              <Link to="/terms" className="hover:text-teal-600 transition-colors">
                Terms of Service
              </Link>
              <Link to="/privacy" className="hover:text-teal-600 transition-colors">
                Privacy Policy
              </Link>
              <a href="mailto:contact@appslabllc.com" className="hover:text-teal-600 transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
